import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileDown, FileText, Search, Loader2 } from "lucide-react";

const fmt = (d) => {
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

function presetRange(p) {
  const now = new Date();
  const today = fmt(now);
  if (p === "today") return [today, today];
  if (p === "yesterday") {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    return [fmt(y), fmt(y)];
  }
  if (p === "last7") {
    const s = new Date(now);
    s.setDate(s.getDate() - 6);
    return [fmt(s), today];
  }
  if (p === "month") {
    const s = new Date(now.getFullYear(), now.getMonth(), 1);
    return [fmt(s), today];
  }
  return ["", ""]; // all / custom
}

const PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "month", label: "This Month" },
  { value: "all", label: "All Time" },
  { value: "custom", label: "Custom Range" },
];

const BASIS_LABEL = { created_at: "Creation Date", event_date: "Event Date" };

export default function ReportsPanel() {
  const [preset, setPreset] = useState("all");
  const [basis, setBasis] = useState("created_at");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const { user } = useAuth();
  const canExport = user.role === "management";

  const buildParams = (f, t, b) => {
    const p = { date_basis: b };
    if (f) p.date_from = f;
    if (t) p.date_to = t;
    return p;
  };

  const run = async (f = from, t = to, b = basis) => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/summary", { params: buildParams(f, t, b) });
      setSummary(data);
    } catch {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const onPreset = (p) => {
    setPreset(p);
    if (p !== "custom") {
      const [f, t] = presetRange(p);
      setFrom(f);
      setTo(t);
      run(f, t, basis);
    }
  };

  const onBasis = (b) => {
    setBasis(b);
    run(from, to, b);
  };

  const onCustomRun = () => {
    setPreset("custom");
    run();
  };

  const download = async (fmtType) => {
    setExporting(fmtType);
    try {
      const res = await api.get(`/reports/export/${fmtType}`, {
        params: buildParams(from, to, basis),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tamanna_leads_report.${fmtType}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmtType.toUpperCase()} exported`);
    } catch {
      toast.error(`Failed to export ${fmtType.toUpperCase()}`);
    } finally {
      setExporting("");
    }
  };

  React.useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rangeText = from || to ? `${from || "…"} → ${to || "…"}` : "All time";

  return (
    <div className="space-y-5">
      <Card className="p-5 border-slate-200 shadow-sm">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="space-y-2">
              <Label>Quick Range</Label>
              <Select value={preset} onValueChange={onPreset}>
                <SelectTrigger className="w-[170px]" data-testid="report-preset-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Filter By</Label>
              <Select value={basis} onValueChange={onBasis}>
                <SelectTrigger className="w-[160px]" data-testid="report-basis-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Creation Date</SelectItem>
                  <SelectItem value="event_date">Event Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>From</Label>
              <Input
                type="date"
                value={from}
                onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }}
                data-testid="report-from-date"
              />
            </div>
            <div className="space-y-2">
              <Label>To</Label>
              <Input
                type="date"
                value={to}
                onChange={(e) => { setTo(e.target.value); setPreset("custom"); }}
                data-testid="report-to-date"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={onCustomRun} className="bg-[#1E3A8A] hover:bg-[#172554]" data-testid="run-report-button">
                <Search className="h-4 w-4 mr-1.5" /> Run
              </Button>
              {canExport && (<>
              <Button variant="outline" onClick={() => download("csv")} disabled={exporting === "csv"} data-testid="export-csv-button">
                {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}
                CSV
              </Button>
              <Button variant="outline" onClick={() => download("pdf")} disabled={exporting === "pdf"} data-testid="export-pdf-button">
                {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />}
                PDF
              </Button>
              </>)}
            </div>
          </div>
          <p className="text-xs text-slate-400" data-testid="report-active-range">
            Showing <span className="font-medium text-slate-600">{rangeText}</span> by{" "}
            <span className="font-medium text-slate-600">{BASIS_LABEL[basis]}</span>
          </p>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="font-heading font-semibold text-slate-900">Executive Summary Matrix</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Lead counts grouped by Sales Executive for the selected range
          </p>
        </div>
        {loading ? (
          <div className="py-12 flex justify-center text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : summary ? (
          <Table data-testid="report-summary-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Sales Executive</TableHead>
                <TableHead className="text-center">Pending</TableHead>
                <TableHead className="text-center">Failed</TableHead>
                <TableHead className="text-center">Success</TableHead>
                <TableHead className="text-center">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.rows.map((r) => (
                <TableRow key={r.username}>
                  <TableCell className="font-medium text-slate-900">{r.name}</TableCell>
                  <TableCell className="text-center font-mono text-amber-600">{r.pending}</TableCell>
                  <TableCell className="text-center font-mono text-rose-600">{r.failed}</TableCell>
                  <TableCell className="text-center font-mono text-emerald-600">{r.success}</TableCell>
                  <TableCell className="text-center font-mono font-semibold">{r.total}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-blue-50/50 font-semibold">
                <TableCell className="text-[#1E3A8A]">Grand Total</TableCell>
                <TableCell className="text-center font-mono text-amber-700">{summary.grand_total.pending}</TableCell>
                <TableCell className="text-center font-mono text-rose-700">{summary.grand_total.failed}</TableCell>
                <TableCell className="text-center font-mono text-emerald-700">{summary.grand_total.success}</TableCell>
                <TableCell className="text-center font-mono text-[#1E3A8A]">{summary.grand_total.total}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : null}
      </Card>
    </div>
  );
}
